/** Translate old bookmarks without losing their filters or post-login context. */
const paths: [RegExp, string][] = [
  [/^\/(dashboard|app)\/?$/, '/'],
  [/^\/dashboard\/calls\/?$/, '/calls'],
  [/^\/dashboard\/callers\/?$/, '/contacts'],
  [/^\/dashboard\/employees\/?$/, '/team'],
  [/^\/dashboard\/excluded\/?$/, '/exclusions'],
  [/^\/dashboard\/storage\/?$/, '/recordings'],
  [/^\/dashboard\/plans\/?$/, '/billing'],
  [/^\/dashboard\/transcription\/?$/, '/ai'],
  [/^\/dashboard\/webhooks\/?$/, '/webhooks'],
  [/^\/dashboard\/api-access\/?$/, '/api-apps'],
  [/^\/dashboard\/settings\/?$/, '/account'],
  [/^\/dashboard\/onboarding\/?$/, '/welcome'],
  [/^\/(dashboard\/)?reports\/missed\/?$/, '/reports?view=missed'],
  [/^\/(dashboard\/)?reports\/(performance|employees?|employee\/[^/]+)\/?$/, '/reports?view=team'],
  [/^\/(dashboard\/)?reports\/overview\/?$/, '/reports'],
  [/^\/dashboard\/reports\/?$/, '/reports'],
  [/^\/app\//, '/'],
];

const aliases: Record<string, string> = {
  search: 'q', direction: 'type', dateRange: 'range', selectedEmployee: 'employee', connected: 'connection',
};
const presets: Record<string, string> = { '7days': 'last_7_days', '30days': 'last_30_days', all_time: 'all' };

export function legacyDestination(pathname: string, search = '', hash = '', now = new Date()): string | null {
  const match = paths.find(([re]) => re.test(pathname));
  const target = new URL(match?.[1] || pathname, 'https://callsync.invalid');
  const params = new URLSearchParams(search);
  let changed = Boolean(match);
  const filters = ['/calls', '/contacts', '/reports'].includes(target.pathname);
  if (filters) {
    for (const [oldKey, key] of Object.entries(aliases)) {
      if (!params.has(oldKey)) continue;
      if (!params.has(key)) params.set(key, params.get(oldKey)!);
      params.delete(oldKey);
      changed = true;
    }
    const date = params.get('date');
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      if (!params.has('range')) params.set('range', 'custom');
      if (!params.has('from')) params.set('from', date);
      if (!params.has('to')) params.set('to', date);
      params.delete('date');
      changed = true;
    }
    const custom = params.get('customRange');
    if (custom) {
      try {
        const range = JSON.parse(custom);
        for (const [oldKey, key] of [['startDate', 'from'], ['endDate', 'to']]) {
          if (!params.has(key) && typeof range?.[oldKey] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(range[oldKey]))
            params.set(key, range[oldKey]);
        }
        if (!params.has('range') && (params.has('from') || params.has('to'))) params.set('range', 'custom');
      } catch { /* Ignore a malformed old custom-range value. */ }
      params.delete('customRange');
      changed = true;
    }
    const range = params.get('range') || '';
    if (presets[range]) { params.set('range', presets[range]); changed = true; }
    if (['3days', '14days'].includes(range)) {
      const end = new Date(now); const start = new Date(now);
      start.setDate(start.getDate() - (range === '3days' ? 2 : 13));
      const localDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      params.set('range', 'custom');
      if (!params.has('from')) params.set('from', localDate(start));
      if (!params.has('to')) params.set('to', localDate(end));
      changed = true;
    }
    for (const key of ['employee', 'type', 'connection']) {
      if (params.get(key) === 'all') { params.delete(key); changed = true; }
    }
    const sort = params.get('sortConfig');
    if (sort) {
      try {
        const value = JSON.parse(sort);
        const keys: Record<string,string> = { date: 'call_time', name: 'caller_name', duration: 'duration', employee: 'employee_name', call_time: 'call_time', caller_name: 'caller_name', employee_name: 'employee_name' };
        if (!params.has('sort') && keys[value?.key]) params.set('sort', keys[value.key]);
        if (!params.has('dir') && ['asc','desc'].includes(value?.direction)) params.set('dir', value.direction);
      } catch { /* Invalid sort data must not stop navigation. */ }
      params.delete('sortConfig'); changed = true;
    }
  }
  // A path-specific report view takes precedence over a stale query view.
  target.searchParams.forEach((value, key) => params.set(key, value));
  const employee = pathname.match(/^\/(?:dashboard\/)?reports\/employee\/([^/]+)\/?$/)?.[1];
  if (employee) {
    try { params.set('employee', decodeURIComponent(employee)); } catch { params.set('employee', employee); }
  }
  if (!changed) return null;
  const query = params.toString();
  return `${target.pathname}${query ? `?${query}` : ''}${hash}`;
}
